import PropTypes from 'prop-types';
import { useState, useRef, Fragment } from 'react';
import { Box, Button, ButtonGroup, MenuItem, MenuList, Paper, Popper, Grow, ClickAwayListener, CircularProgress, Divider, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import Iconify from 'src/components/Iconify';

// SplitActionButton — [Main Label | ▾] split-button.
// menuItems: [{ label, onClick, icon?, header?, divider? }]
//   - header=true   — render a non-clickable subheader row (e.g. "Filtered by NEW status")
//   - divider=true  — render a horizontal divider at this position (label/onClick ignored)
//   Otherwise an actionable MenuItem is rendered.
export default function SplitActionButton({
    mainLabel,
    mainIcon,
    mainColor,
    disabled,
    loading,
    onMainClick,
    menuItems,
}) {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);

    const handleArrowClick = () => setOpen((prev) => !prev);

    const handleClose = (e) => {
        if (anchorRef.current?.contains(e?.target)) return;
        setOpen(false);
    };

    return (
        <>
            <ButtonGroup
                ref={anchorRef}
                variant="contained"
                size="medium"
                disabled={disabled || loading}
                disableElevation
                sx={{
                    width: '100%',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    '& .MuiButtonGroup-grouped': {
                        borderColor: `${alpha(mainColor, 0.24)} !important`,
                    },
                }}
            >
                <Button
                    onClick={onMainClick}
                    startIcon={loading ? <CircularProgress size={16} /> : <Iconify icon={mainIcon} width={18} />}
                    sx={{
                        flex: 1,
                        height: 44,
                        borderRadius: '12px 0 0 12px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        justifyContent: 'flex-start',
                        px: 2,
                        bgcolor: alpha(mainColor, 0.10),
                        color: mainColor,
                        boxShadow: 'none',
                        '&:hover': { bgcolor: alpha(mainColor, 0.18), boxShadow: 'none' },
                    }}
                >
                    {mainLabel}
                </Button>

                {menuItems.length > 0 && (
                    <Button
                        size="small"
                        aria-label="more options"
                        onClick={handleArrowClick}
                        sx={{
                            flex: '0 0 36px',
                            width: 36,
                            minWidth: 36,
                            maxWidth: 36,
                            height: 44,
                            borderRadius: '0 12px 12px 0',
                            bgcolor: alpha(mainColor, 0.10),
                            color: mainColor,
                            boxShadow: 'none',
                            '&:hover': { bgcolor: alpha(mainColor, 0.18), boxShadow: 'none' },
                        }}
                    >
                        <Iconify icon="eva:arrow-ios-downward-fill" width={16} />
                    </Button>
                )}
            </ButtonGroup>

            <Popper
                open={open}
                anchorEl={anchorRef.current}
                placement="bottom-end"
                transition
                disablePortal
                style={{ zIndex: 1300, width: anchorRef.current?.offsetWidth || 240 }}
            >
                {({ TransitionProps }) => (
                    <Grow {...TransitionProps}>
                        <Paper elevation={8} sx={{ borderRadius: '10px', overflow: 'hidden', mt: 0.5 }}>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MenuList dense sx={{ py: 0.5 }}>
                                    {menuItems.map((item, idx) => {
                                        const key = item.id ?? `${item.label || 'item'}-${idx}`;
                                        if (item.divider) {
                                            return <Divider key={key} sx={{ my: 0.25 }} />;
                                        }
                                        if (item.header) {
                                            return (
                                                <Box
                                                    key={key}
                                                    sx={{
                                                        px: 2,
                                                        py: 0.5,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.75,
                                                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                                                    }}
                                                >
                                                    {item.icon && <Iconify icon={item.icon} width={13} sx={{ color: 'primary.main' }} />}
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            fontSize: '0.7rem',
                                                            fontWeight: 700,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.04em',
                                                            color: 'text.secondary',
                                                        }}
                                                    >
                                                        {item.label}
                                                    </Typography>
                                                </Box>
                                            );
                                        }
                                        return (
                                            <Fragment key={key}>
                                                {idx > 0 && !menuItems[idx - 1]?.header && !menuItems[idx - 1]?.divider && <Divider />}
                                                <MenuItem
                                                    onClick={() => {
                                                        setOpen(false);
                                                        item.onClick();
                                                    }}
                                                    sx={{ fontSize: '0.83rem', py: 0.75, px: 2, gap: 1 }}
                                                >
                                                    {item.icon && <Iconify icon={item.icon} width={15} />}
                                                    {item.label}
                                                </MenuItem>
                                            </Fragment>
                                        );
                                    })}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </>
    );
}

SplitActionButton.propTypes = {
    mainLabel: PropTypes.string.isRequired,
    mainIcon: PropTypes.string.isRequired,
    mainColor: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    loading: PropTypes.bool,
    onMainClick: PropTypes.func.isRequired,
    menuItems: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.string,
        onClick: PropTypes.func,
        icon: PropTypes.string,
        header: PropTypes.bool,
        divider: PropTypes.bool,
    })),
};

SplitActionButton.defaultProps = {
    disabled: false,
    loading: false,
    menuItems: [],
};
